import React from 'react';
import { Button } from './button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

/**
 * Pagination component for handling paginated data
 * @param {Object} pagination - Pagination metadata
 * @param {Function} onPageChange - Function to call when page changes
 * @param {Function} onPageSizeChange - Function to call when page size changes
 * @param {boolean} loading - Loading state
 * @param {Array} pageSizeOptions - Available page size options
 */
export function Pagination({
  pagination,
  onPageChange,
  onPageSizeChange,
  loading = false,
  pageSizeOptions = [10, 20, 50, 100],
  showPageSizeSelector = true,
  showPageInfo = true,
  className = ""
}) {
  if (!pagination || pagination.total_items === 0) {
    return null;
  }

  const {
    current_page,
    total_pages,
    total_items,
    per_page,
    has_next,
    has_previous
  } = pagination;

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, current_page - Math.floor(maxVisible / 2));
    let end = Math.min(total_pages, start + maxVisible - 1);
    
    // Adjust start if we're near the end
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const pageNumbers = generatePageNumbers();

  return (
    <div className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 ${className}`}>
      {/* Page info */}
      {showPageInfo && (
        <div className="flex items-center space-x-2 text-sm text-slate-600">
          <span>
            Mostrando {((current_page - 1) * per_page) + 1} até {Math.min(current_page * per_page, total_items)} de {total_items} resultados
          </span>
        </div>
      )}

      {/* Pagination controls */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Previous button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(current_page - 1)}
          disabled={!has_previous || loading}
        >
          ← Anterior
        </Button>

        {/* First page */}
        {pageNumbers[0] > 1 && (
          <>
            <Button
              variant={1 === current_page ? "default" : "outline"}
              size="sm"
              onClick={() => onPageChange(1)}
              disabled={loading}
            >
              1
            </Button>
            {pageNumbers[0] > 2 && <span className="px-2 text-slate-500">...</span>}
          </>
        )}

        {/* Page numbers */}
        {pageNumbers.map((page) => (
          <Button
            key={page}
            variant={page === current_page ? "default" : "outline"}
            size="sm"
            onClick={() => onPageChange(page)}
            disabled={loading}
            className={page === current_page ? "bg-orange-500 text-white" : ""}
          >
            {page}
          </Button>
        ))}

        {/* Last page */}
        {pageNumbers[pageNumbers.length - 1] < total_pages && (
          <>
            {pageNumbers[pageNumbers.length - 1] < total_pages - 1 && (
              <span className="px-2 text-slate-500">...</span>
            )}
            <Button
              variant={total_pages === current_page ? "default" : "outline"}
              size="sm"
              onClick={() => onPageChange(total_pages)}
              disabled={loading}
            >
              {total_pages}
            </Button>
          </>
        )}

        {/* Next button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(current_page + 1)}
          disabled={!has_next || loading}
        >
          Próximo →
        </Button>
      </div>

      {/* Page size selector */}
      {showPageSizeSelector && (
        <div className="flex items-center space-x-2 text-sm">
          <span className="text-slate-600">Por página:</span>
          <Select
            value={per_page.toString()}
            onValueChange={(value) => onPageSizeChange(parseInt(value))}
            disabled={loading}
          >
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

/**
 * Simple pagination component for basic use cases
 */
export function SimplePagination({ 
  currentPage, 
  totalPages, 
  onPageChange, 
  loading = false,
  className = ""
}) {
  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className={`flex items-center justify-center space-x-2 ${className}`}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1 || loading}
      >
        ←
      </Button>
      
      <span className="px-3 py-2 text-sm text-slate-600">
        {currentPage} de {totalPages}
      </span>
      
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages || loading}
      >
        →
      </Button>
    </div>
  );
}

export default Pagination;